import { useEffect, useMemo, useRef, useState } from 'react'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet'
import { ArrowRight, MapPin, X } from 'lucide-react'
import type { Place } from '../../types/place'
import { useT } from '../../i18n/t'
import { PanelThumbnailImage } from './PanelThumbnailImage'

export type PlacePinStatus = 'verified' | 'untouched'

type Props = {
  places: Place[]
  /** Mapa: placeId -> status. Domyślnie 'untouched'. */
  statusByPlaceId?: Record<number, PlacePinStatus>
  /** Wywoływane po kliknięciu CTA "Otwórz" / "Zweryfikuj". Brak = tylko podgląd na mapie. */
  onOpenPlace?: (placeId: number) => void
  /** Wysokość mapy. */
  heightClassName?: string
  /**
   * `publicCatalog` — strona publiczna: bez legendy statusów; wyróżnione miejsce na zielono, reszta z CTA rejestracji.
   */
  displayMode?: 'panel' | 'publicCatalog'
  /** W trybie `publicCatalog`: id miejsca widocznego na mapie (zielona pinezka + pełny podgląd). */
  publicFeaturedPlaceId?: number
  /** W trybie `publicCatalog`: CTA rejestracji dla pozostałych pinezek. */
  onRegister?: () => void
}

function buildPinIcon(status: PlacePinStatus, isSelected: boolean) {
  const cls = ['pds-pin']
  if (status === 'verified') cls.push('pds-pin--verified')
  if (isSelected) cls.push('pds-pin--selected')
  return L.divIcon({
    html: `<span class="${cls.join(' ')}"></span>`,
    className: '',
    iconSize: isSelected ? [34, 34] : [26, 26],
    iconAnchor: isSelected ? [17, 17] : [13, 13],
  })
}

/** Wyśrodkowuje mapę na wybranej pinezce (gdy zmienia się selectedPlace). */
function MapFocus({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap()
  useEffect(() => {
    if (lat === null || lng === null) return
    map.flyTo({ lat, lng }, Math.max(map.getZoom(), 9), { duration: 0.6 })
  }, [lat, lng, map])
  return null
}

export function PlacesPanelMap({
  places,
  statusByPlaceId = {},
  onOpenPlace,
  heightClassName = 'h-[420px] md:h-[520px]',
  displayMode = 'panel',
  publicFeaturedPlaceId,
  onRegister,
}: Props) {
  const { t } = useT()
  const isPublicCatalog = displayMode === 'publicCatalog'
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const closeBtnRef = useRef<HTMLButtonElement | null>(null)

  const center = useMemo(() => {
    if (!places.length) return { lat: 50.9, lng: 16.5 }
    const sumLat = places.reduce((acc, p) => acc + p.lat, 0)
    const sumLng = places.reduce((acc, p) => acc + p.lng, 0)
    return { lat: sumLat / places.length, lng: sumLng / places.length }
  }, [places])

  const selected = useMemo(
    () => (selectedId ? places.find((p) => p.id === selectedId) ?? null : null),
    [selectedId, places],
  )

  const selectedStatus: PlacePinStatus = selected
    ? statusByPlaceId[selected.id] ?? 'untouched'
    : 'untouched'

  const pinStatusForMarker = (placeId: number): PlacePinStatus => {
    if (isPublicCatalog) {
      return publicFeaturedPlaceId != null && placeId === publicFeaturedPlaceId ? 'verified' : 'untouched'
    }
    return statusByPlaceId[placeId] ?? 'untouched'
  }

  const isPublicLockedPlace = (placeId: number) =>
    isPublicCatalog && publicFeaturedPlaceId != null && placeId !== publicFeaturedPlaceId

  return (
    <div
      className={`relative overflow-hidden rounded-lg bg-ink/[0.04] ring-1 ring-ink/10 ${heightClassName}`}
    >
      <div className="h-full w-full">
        <MapContainer
          center={center}
          zoom={9}
          scrollWheelZoom
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          />

          {places.map((p) => {
            const status: PlacePinStatus = pinStatusForMarker(p.id)
            const isSel = selectedId === p.id
            return (
              <Marker
                key={`${p.id}-${status}-${isSel ? 'sel' : ''}`}
                position={{ lat: p.lat, lng: p.lng }}
                icon={buildPinIcon(status, isSel)}
                eventHandlers={{
                  click: () => setSelectedId(p.id),
                }}
                keyboard
              />
            )
          })}

          {selected ? <MapFocus lat={selected.lat} lng={selected.lng} /> : null}
        </MapContainer>
      </div>

      {!isPublicCatalog ? (
        <div className="pointer-events-none absolute left-4 top-4 z-[500] inline-flex items-center gap-3 rounded-lg bg-paper/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/70 ring-1 ring-ink/10 backdrop-blur">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-paper" aria-hidden />
            {t('panel.placesMap.legend.verified')}
          </span>
          <span className="text-ink/20">·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-ink ring-2 ring-paper" aria-hidden />
            {t('panel.placesMap.legend.untouched')}
          </span>
        </div>
      ) : null}

      {selected ? (
        <div
          role="dialog"
          aria-label={
            isPublicLockedPlace(selected.id)
              ? t('landing.gallery.mapLockedTitle')
              : selected.name
          }
          className="pointer-events-auto absolute inset-x-3 bottom-3 z-[600] sm:bottom-4 md:left-auto md:right-4 md:max-w-[360px]"
        >
          <div className="overflow-hidden rounded-lg bg-paper shadow-[0_12px_36px_-8px_rgba(0,0,0,0.35)] ring-1 ring-ink/10">
            {isPublicLockedPlace(selected.id) ? (
              <div className="relative p-4">
                <button
                  ref={closeBtnRef}
                  type="button"
                  onClick={() => setSelectedId(null)}
                  aria-label={t('panel.placesMap.popup.close')}
                  className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-paper/90 text-ink/70 ring-1 ring-ink/10 transition hover:bg-paper hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
                <div className="pr-10">
                  <p className="display text-[20px] leading-tight">
                    {t('landing.gallery.mapLockedTitle')}
                  </p>
                </div>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={() => onRegister?.()}
                    className="group inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-paper transition hover:bg-ink/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
                  >
                    {t('landing.gallery.mapLockedCta')}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
                  </button>
                </div>
              </div>
            ) : (
              <>
                {selected.photos?.[0] ? (
                  <div className="relative h-32 w-full overflow-hidden bg-ink/[0.06]">
                    <PanelThumbnailImage
                      src={
                        typeof selected.photos[0] === 'string'
                          ? selected.photos[0]
                          : selected.photos[0].src
                      }
                      alt={
                        typeof selected.photos[0] === 'string'
                          ? ''
                          : selected.photos[0].alt
                      }
                      widths={[320, 480, 640]}
                      sizes="360px"
                      quality={72}
                      className="h-full w-full object-cover"
                    />
                    <button
                      ref={closeBtnRef}
                      type="button"
                      onClick={() => setSelectedId(null)}
                      aria-label={t('panel.placesMap.popup.close')}
                      className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-paper/90 text-ink/70 ring-1 ring-ink/10 transition hover:bg-paper hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ) : (
                  <button
                    ref={closeBtnRef}
                    type="button"
                    onClick={() => setSelectedId(null)}
                    aria-label={t('panel.placesMap.popup.close')}
                    className="absolute right-3 top-3 z-10 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-paper/90 text-ink/70 ring-1 ring-ink/10 transition hover:bg-paper hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                )}

                <div className="p-4">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/55">
                    {selected.category} · {selected.region}
                  </div>
                  <div className="mt-1.5 display text-[20px] leading-tight">
                    {selected.name}
                  </div>
                  <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-ink/70 line-clamp-3">
                    {selected.description}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-ink/55">
                    <MapPin className="h-3 w-3 text-ink/45" aria-hidden />
                    {selected.locationLabel}
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    {onOpenPlace ? (
                      <button
                        type="button"
                        onClick={() => onOpenPlace(selected.id)}
                        className="group inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-paper transition hover:bg-ink/85 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"
                      >
                        {isPublicCatalog
                          ? t('landing.gallery.mapOpenPlace')
                          : selectedStatus === 'verified'
                            ? t('panel.placesMap.cta.open')
                            : t('panel.placesMap.cta.verify')}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden />
                      </button>
                    ) : null}
                    {!isPublicCatalog && selectedStatus === 'verified' ? (
                      <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                        {t('panel.placesMap.legend.verified')}
                      </span>
                    ) : null}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="pointer-events-none absolute inset-x-3 bottom-3 z-[500] sm:bottom-4 md:left-auto md:right-4 md:max-w-[360px]">
          <div className="rounded-lg bg-paper/90 px-4 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/55 ring-1 ring-ink/10 backdrop-blur">
            {isPublicCatalog ? t('landing.gallery.mapHint') : t('panel.placesMap.hint')}
          </div>
        </div>
      )}
    </div>
  )
}
